import React from 'react'
import PropTypes from 'prop-types'
import { HeaderFour, ProgressStatusMedium } from 'secondstep-components'
import CreateCourse from 'experienceComponents/CreateCourse'
import ProgressBar from 'components/ProgressBar'
import { pluralizeWord } from 'utils/stringHelpers'
import { getCourseInstancePath } from './helpers'

import {
  CardWrapper,
  GoalDot,
  GoalText,
  InfoBox,
  ProgressSection,
  StyledGradeTile,
  Wrapper,
  TopSection,
} from './component.styles'

const ClassCard = React.forwardRef(
  (
    {
      className,
      dataTestId,
      defaultUnit,
      displayTitle,
      gradeDisplayTitle = '',
      instance,
      goalPercent,
      program,
      onBlur,
      onClick,
      onFocus,
      scoID,
      setFocus,
      includeCreateCourseMenu = true,
      includeProgressBar = false,
      tier = 1,
      totalAmount,
      totalComplete,
    },
    ref,
  ) => {
    const course = {
      instance,
      scoId: scoID,
      title: displayTitle,
    }
    const courseInstancePath = getCourseInstancePath({
      program,
      gradeDisplayTitle,
      instance,
      tier,
      defaultUnit,
    })

    // Calculate progress percentage
    const percentComplete =
      totalAmount > 0 ? Math.round((totalComplete / totalAmount) * 100) : 0

    // Derive goalValue from goalPercent (percentage) and totalAmount
    // goalPercent is a percentage (e.g., 80 means 80%)
    // goalValue is the number of lessons (e.g., if totalAmount is 10 and goal is 80%, goalValue is 8)
    // Minimum of 1 lesson if goalPercent is set but rounds to 0
    const goalValue =
      goalPercent && totalAmount > 0
        ? Math.max(1, Math.round((goalPercent / 100) * totalAmount))
        : null

    // Calculate discrete goalPercentage based on actual lesson count
    // This ensures the goal dot moves by discrete lesson increments, not continuous percentage
    const discreteGoalPercentage =
      goalValue && totalAmount > 0 ? (goalValue / totalAmount) * 100 : null

    return (
      <Wrapper className="class-card" onBlur={onBlur} onFocus={onFocus}>
        <CardWrapper
          className={className}
          dataTestId={dataTestId}
          includeProgressBar={includeProgressBar}
          onClick={() => onClick(courseInstancePath)}
          tier={tier}
        >
          <TopSection>
            <StyledGradeTile
              dataTestId="classGrade"
              displayText={gradeDisplayTitle}
              isStatic
              size="small"
              tier={tier}
            />
            <InfoBox>
              {displayTitle && (
                <HeaderFour dataTestId="className">{displayTitle}</HeaderFour>
              )}
              <ProgressStatusMedium
                dataTestId={'classProgress'}
                totalAmount={tier === 2 ? null : totalAmount}
                totalComplete={totalComplete}
              />
            </InfoBox>
          </TopSection>
          {includeProgressBar && (
            <ProgressSection>
              <ProgressBar
                dataTestId="classProgressBar"
                hideProgressText={true}
                percentComplete={percentComplete}
              />
              {goalValue && <GoalDot goalPercentage={discreteGoalPercentage} />}
              {goalValue && (
                <GoalText>
                  Goal: {goalValue}{' '}
                  {pluralizeWord(goalValue, 'Lesson', 'Lessons')}
                </GoalText>
              )}
            </ProgressSection>
          )}
        </CardWrapper>
        {includeCreateCourseMenu && (
          <CreateCourse
            course={course}
            courseInstanceName={displayTitle}
            edit
            isClassCardMenu
            key={`create-course-${displayTitle}-${scoID}`}
            ref={ref}
            setFocus={setFocus}
          />
        )}
      </Wrapper>
    )
  },
)

ClassCard.propTypes = {
  className: PropTypes.string,
  dataTestId: PropTypes.string.isRequired,
  defaultUnit: PropTypes.string,
  displayTitle: PropTypes.string,
  goalPercent: PropTypes.number,
  gradeDisplayTitle: PropTypes.string,
  includeCreateCourseMenu: PropTypes.bool,
  includeProgressBar: PropTypes.bool,
  instance: PropTypes.string,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  program: PropTypes.string,
  scoID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setFocus: PropTypes.func,
  tier: PropTypes.number,
  totalAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalComplete: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}
ClassCard.displayName = 'ClassCardForwardedRef'
export default ClassCard
